import { logError } from "src/errorHandling/errorLogging.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import {
	PartConfiguratorFormApi,
	PartConfiguratorFormApi_GetConfiguratorTreeModelResponse,
} from "src/api/generated/erp/configurator/api/partConfiguratorFormApi.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useRef, useState } from "react";
import i18n from "i18next";
import {
	PART_CONFIGURATION_NOT_SAVED,
	PartConfiguratorTreeStatefulView,
	PartConfiguratorTreeStatefulViewProps,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { getStoredConfiguratorTreeModelOrCreateNew } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/configuratorTreeModelStorage.ts";
import { ConfiguratorTreeServerErrorView } from "src/components/views/erp/configurator/configuratorForm/ConfiguratorTreeServerErrorView.tsx";
import { ConfiguratorTreeModelSource } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeModelSource.ts";
import { CustomAppViewOverrideFrame } from "src/components/views/customApps/CustomAppViewOverrideFrame.tsx";
import {
	CUSTOM_APP_VIEW_EVENT_NOT_ACCEPTED,
	CustomAppViewOverride,
} from "src/customApps/runtime/customAppHostBridge.ts";
import { ConfiguratorAppAccess } from "src/api/generated/erp/configurator/configuratorApp/configuratorAppAccess.ts";
import {
	PropertyModelConfiguratorView,
	PropertyModelConfiguratorViewProps,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/PropertyModelConfiguratorView.tsx";
import {
	getPartConfigurationCurrentInput,
	getPartConfigurationSchema,
	getPartConfigurationTarget,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSchemaClient.ts";
import {
	EvaluatePartConfiguration,
	loadPartConfigurationEvaluator,
	PartConfigurationSchema,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import { PropertyInputValues } from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";
import { defaultCheckpointState } from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";
import { PropertyModelSubConfigurationImportAction } from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelSubConfigurationImport.ts";

/** Component versions use the component form and property-model versions the generic property form. */
type PartConfiguratorInitData =
	| { inputModel: "COMPONENT"; componentInitData: PartConfiguratorFormApi_GetConfiguratorTreeModelResponse }
	| {
			inputModel: "PROPERTY";
			/** Identifies one load, so that a reset starts the form again. */
			loadId: string;
			schema: PartConfigurationSchema;
			evaluatePartConfiguration: EvaluatePartConfiguration;
			inputValues: PropertyInputValues;
			hasSavedConfiguration: boolean;
	  };

export type PartConfiguratorTopLevelViewProps = GenericPartConfiguratorTopLevelViewProps;

/**
 * Opens the product family's configurator Custom App instead of the generic form when the family has one. The app
 * completes the form with its values, which `onCompleted` saves as the form's own, and closing it cancels the form.
 */
export function PartConfiguratorTopLevelView(props: PartConfiguratorTopLevelViewProps) {
	const { configuratorType, readonly, onCompleted, onDataDirtyStateChanged } = props;
	const showConfirmDialog = useConfirmDialog();

	return (
		<AsyncFetchRender
			fetch={fetchConfiguratorApp}
			content={(configuratorApp) => {
				if (configuratorApp === "declined") return null;
				if (configuratorApp == null) return <GenericPartConfiguratorTopLevelView {...props} />;
				return (
					<ConfiguratorAppView
						configuratorApp={configuratorApp}
						onCompleted={onCompleted}
						onDataDirtyStateChanged={onDataDirtyStateChanged}
					/>
				);
			}}
		/>
	);

	/** The family's configurator app, or "declined" when the user did not reconfigure a manually edited one. */
	async function fetchConfiguratorApp(): Promise<ConfiguratorAppAccess | "declined" | null> {
		if (readonly) return null;
		const configuratorApp = await PartConfiguratorFormApi.getConfiguratorAppAccess({ configuratorType });
		if (configuratorApp == null) return null;
		if (await confirmConfiguringManuallyEditedConfiguration()) return configuratorApp;
		await onCompleted?.({ type: "cancel", isEdited: false });
		return "declined";
	}

	/** Asks like the generic form before a manually edited configuration is configured again. */
	async function confirmConfiguringManuallyEditedConfiguration(): Promise<boolean> {
		const hasBeenEditedManually = await PartConfiguratorFormApi.getConfigurationHasBeenEditedManually({
			configuratorType,
		});
		if (!hasBeenEditedManually) return true;
		return await showConfirmDialog({ message: i18n.t("confirm_configure_manually_edited_configuration") });
	}
}

interface ConfiguratorAppViewProps extends Pick<
	PartConfiguratorTopLevelViewProps,
	"onCompleted" | "onDataDirtyStateChanged"
> {
	configuratorApp: ConfiguratorAppAccess;
}

/** The family's configurator app in place of the form. Completing it saves like the form, and closing it cancels. */
function ConfiguratorAppView({ configuratorApp, onCompleted, onDataDirtyStateChanged }: ConfiguratorAppViewProps) {
	const [configurationSessionId] = useState(() => crypto.randomUUID());
	const viewOverride: CustomAppViewOverride = {
		view: "partConfiguratorForm",
		handlers: {
			complete: async ({ valuesByPropertyName }) => {
				const completionResult = await onCompleted?.({
					type: "success",
					value: {
						propertyValues: undefined,
						propertyInputValues: valuesByPropertyName,
						configuratorInput: { inputComponentValues: [] },
						configurationSessionId,
						checkpointState: defaultCheckpointState,
					},
				});
				return completionResult === PART_CONFIGURATION_NOT_SAVED ?
						CUSTOM_APP_VIEW_EVENT_NOT_ACCEPTED
					:	undefined;
			},
		},
	};

	return (
		<CustomAppViewOverrideFrame
			access={configuratorApp.access}
			customAppId={configuratorApp.customAppId}
			appName={configuratorApp.customAppName}
			viewOverride={viewOverride}
			// The app's own unsaved changes are checked before the dialog closes.
			onRequestClose={() => void onCompleted?.({ type: "cancel", isEdited: true })?.catch(logError)}
			onDataDirtyStateChanged={onDataDirtyStateChanged}
		/>
	);
}

interface GenericPartConfiguratorTopLevelViewProps
	extends
		Omit<
			PartConfiguratorTreeStatefulViewProps,
			"initialTreeModel" | "treeModelStorageKey" | "hasSavedPartConfiguration" | "validationEnabled"
		>,
		Pick<PropertyModelConfiguratorViewProps, "propertyModelFooterExtraComponents"> {
	propertyModelSubConfigurationImportAction?: PropertyModelSubConfigurationImportAction;
}

const GenericPartConfiguratorTopLevelView = (props: GenericPartConfiguratorTopLevelViewProps) => {
	const {
		configuratorType,
		onCompleted,
		serverErrorCustomContent,
		onDataDirtyStateChanged,
		propertyModelFooterExtraComponents,
		propertyModelSubConfigurationImportAction,
		...other
	} = props;

	const showConfirmDialog = useConfirmDialog();

	const configurationAfterManualEditConfirmed = useRef(false);

	return (
		<AsyncFetchRender<PartConfiguratorInitData, ConfiguratorTreeModelSource>
			fetch={getInitData}
			errorContent={({ error, reload }) => (
				<ConfiguratorTreeServerErrorView
					configuratorType={configuratorType}
					error={error}
					reload={async () => await reload?.(undefined)}
					customContent={serverErrorCustomContent}
					resetToExistingConfiguration={async () => {
						await PartConfiguratorFormApi.clearSavedConfiguratorTreeModel({ configuratorType });
						await reload?.("SAVED_CONFIGURATION");
					}}
					resetToDefaultValues={async () => {
						await PartConfiguratorFormApi.clearSavedConfiguratorTreeModel({ configuratorType });
						await reload?.("SCRATCH");
					}}
				/>
			)}
			content={(initData, reload) => {
				if (initData.inputModel === "PROPERTY") {
					return (
						<PropertyModelConfiguratorView
							key={initData.loadId}
							configuratorType={configuratorType}
							schema={initData.schema}
							evaluatePartConfiguration={initData.evaluatePartConfiguration}
							initialInputValues={initData.inputValues}
							hasSavedConfiguration={initData.hasSavedConfiguration}
							reloadInitData={(source) => reload(undefined, source)}
							onSubmit={other.onSubmit}
							onCompleted={onCompleted}
							readonly={other.readonly}
							hideFooter={other.hideFooter}
							onDataDirtyStateChanged={onDataDirtyStateChanged}
							propertyModelFooterExtraComponents={propertyModelFooterExtraComponents}
							subConfigurationImportAction={propertyModelSubConfigurationImportAction}
						/>
					);
				}
				const { treeModel, storageKey, savedConfigurationId, validationEnabled } = initData.componentInitData;
				return (
					<PartConfiguratorTreeStatefulView
						{...other}
						configuratorType={configuratorType}
						initialTreeModel={treeModel}
						treeModelStorageKey={storageKey ?? undefined}
						hasSavedPartConfiguration={savedConfigurationId != null}
						serverErrorCustomContent={serverErrorCustomContent}
						onCompleted={onCompleted}
						validationEnabled={validationEnabled}
						onDataDirtyStateChanged={onDataDirtyStateChanged}
					/>
				);
			}}
		/>
	);

	async function getInitData(treeModelSource?: ConfiguratorTreeModelSource): Promise<PartConfiguratorInitData> {
		if (!configurationAfterManualEditConfirmed.current) {
			const hasBeenEditedManually = await PartConfiguratorFormApi.getConfigurationHasBeenEditedManually({
				configuratorType: configuratorType,
			});

			if (hasBeenEditedManually) {
				const confirmed = await showConfirmDialog({
					message: i18n.t("confirm_configure_manually_edited_configuration"),
				});
				if (!confirmed) {
					await onCompleted?.({
						type: "cancel",
						isEdited: false,
					});
				}
			}
			configurationAfterManualEditConfirmed.current = true;
		}

		const inputModel = await PartConfiguratorFormApi.getConfiguratorInputModel({ configuratorType });
		if (inputModel === "PROPERTY") return await getPropertyModelInitData(treeModelSource);

		const componentInitData = await getStoredConfiguratorTreeModelOrCreateNew({
			configuratorType: configuratorType,
			source: treeModelSource,
		});
		return { inputModel: "COMPONENT", componentInitData };
	}

	async function getPropertyModelInitData(
		treeModelSource: ConfiguratorTreeModelSource | undefined,
	): Promise<PartConfiguratorInitData> {
		const target = getPartConfigurationTarget(configuratorType);
		if (target == null) throw new Error(i18n.t("property_model_configurator_target_not_supported"));

		const [schema, currentInput, evaluator] = await Promise.all([
			getPartConfigurationSchema(target),
			getPartConfigurationCurrentInput(target),
			loadPartConfigurationEvaluator(),
		]);
		await evaluator.prepareRuleEngine(schema);
		return {
			inputModel: "PROPERTY",
			loadId: crypto.randomUUID(),
			schema,
			evaluatePartConfiguration: evaluator.evaluatePartConfiguration,
			inputValues: treeModelSource === "SCRATCH" ? {} : (currentInput.valuesByPropertyName ?? {}),
			hasSavedConfiguration: currentInput.partConfigurationId != null,
		};
	}
};
